import { Alert, type AlertTone } from './Alert';
import { Button } from './Button';
import { isApiError, type ApiErrorKind } from '@/lib/api/errors';

/*
  One rendering for a failed request, so every page reports the same failure the same way.

  The point of this component is that the failure kinds are NOT collapsed. "Could not reach
  the server", "the server refused this", "that record is gone" and "you need a fresh second
  factor" lead to four different next actions, and a page that prints "Error" for all four has
  told the technician nothing. So each kind gets its own title, its own tone, and its own
  sentence about what to do; the server's own detail text is shown underneath when there is
  one, rather than instead of the guidance.

  Retry is offered only where retrying can work. A 403 does not become a 200 on the second
  press, and offering the button anyway teaches people to mash it.
*/

interface KindMeta {
  readonly title: string;
  readonly tone: AlertTone;
  readonly guidance: string;
  readonly retryable: boolean;
}

const KIND_META: Readonly<Record<ApiErrorKind, KindMeta>> = {
  network: {
    title: 'Could not reach the JIT server',
    tone: 'offline',
    guidance:
      'Nothing was sent. Check the connection to the control plane, then try again. This is a transport failure, not a refusal.',
    retryable: true,
  },
  unauthenticated: {
    title: 'Your session is no longer valid',
    tone: 'warning',
    guidance: 'Sign in again. Access tokens live ten minutes and are refreshed from a cookie that has now expired or been revoked.',
    retryable: false,
  },
  forbidden: {
    title: 'You do not have permission to see this',
    tone: 'warning',
    guidance:
      'Your role assignments do not cover this record. Ask an administrator for a role at the scope that contains it.',
    retryable: false,
  },
  not_found: {
    title: 'That record does not exist',
    tone: 'warning',
    guidance:
      'It may have been archived or revoked. A record in another tenant looks identical to one that was never there, on purpose.',
    retryable: false,
  },
  conflict: {
    title: 'That conflicts with the current state',
    tone: 'warning',
    guidance: 'Reload to see what changed, then decide again. Nothing was written.',
    retryable: true,
  },
  validation: {
    title: 'The request was rejected',
    tone: 'danger',
    guidance: 'Correct the highlighted fields and submit again. Nothing was written.',
    retryable: false,
  },
  rate_limited: {
    title: 'Too many requests',
    tone: 'warning',
    guidance: 'The server is rate limiting this account. Wait a moment before trying again.',
    retryable: true,
  },
  step_up_required: {
    title: 'A fresh second factor is required',
    tone: 'warning',
    guidance:
      'This action is marked dangerous, so it needs an MFA assertion from the last five minutes. Re-authenticate and repeat it.',
    retryable: false,
  },
  server: {
    title: 'The server failed to answer',
    tone: 'danger',
    guidance: 'This is a fault on the control plane, not in this request. Retry, and check the server logs if it persists.',
    retryable: true,
  },
  invalid_credentials: {
    title: 'Those details did not match',
    tone: 'danger',
    guidance: 'Check the email address and password and try again.',
    retryable: false,
  },
  account_locked: {
    title: 'That account is temporarily locked',
    tone: 'locked',
    guidance: 'Wait for the lock to expire, or ask an administrator to clear it.',
    retryable: false,
  },
  mfa_required: {
    title: 'A second factor is required',
    tone: 'warning',
    guidance: 'Enter the code from your authenticator app.',
    retryable: false,
  },
  mfa_invalid: {
    title: 'That code was not accepted',
    tone: 'danger',
    guidance: 'Wait for the next code and enter it. Each code is accepted once.',
    retryable: false,
  },
  unknown: {
    title: 'Something went wrong',
    tone: 'danger',
    guidance: 'The failure did not match anything this console knows how to explain. Retry, and report it if it repeats.',
    retryable: true,
  },
};

const NON_API_META: KindMeta = {
  title: 'Something went wrong',
  tone: 'danger',
  guidance: 'This failed before it reached the network. Reload the page; report it if it repeats.',
  retryable: true,
};

export interface ApiErrorAlertProps {
  readonly error: unknown;
  /** Prefix for the title, for example "Could not load device". */
  readonly context?: string;
  readonly onRetry?: (() => void) | undefined;
  readonly className?: string;
}

export function ApiErrorAlert({
  error,
  context,
  onRetry,
  className,
}: ApiErrorAlertProps): React.JSX.Element {
  const meta = isApiError(error) ? KIND_META[error.kind] : NON_API_META;
  const detail = isApiError(error)
    ? (error.problem.detail ?? error.problem.title ?? error.message)
    : error instanceof Error
      ? error.message
      : String(error);

  return (
    <Alert
      tone={meta.tone}
      liveness="assertive"
      title={context === undefined ? meta.title : `${context}: ${meta.title.toLowerCase()}`}
      className={className}
    >
      <p>{meta.guidance}</p>
      {detail !== '' && detail !== meta.title ? (
        <p className="mt-1 opacity-80">{detail}</p>
      ) : null}
      {meta.retryable && onRetry !== undefined ? (
        <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Alert>
  );
}
